import React, { useState, useCallback, useMemo } from 'react';
import { Redirect, useHistory } from 'react-router';
import { t } from 'ttag';
import { CircularProgress, Box } from '@material-ui/core';

// Import unified components
import { AuthLayout, AuthLink, AuthMessage } from '../UnifiedComponents';

// Import custom hooks
import { useInviteInfo } from './hooks/useInviteInfo';
import { useUserPreferences } from './hooks/useUserPreferences';
import { useInviteActions } from './hooks/useInviteActions';
import { use2FA } from './hooks/use2FA';
import { useExistingUserFlow } from './hooks/useExistingUserFlow';

// Import components
import { NewUserForm } from './components/NewUserForm';
import { ExistingUserForm } from './components/ExistingUserForm';
import { UserSwitchDialog } from './components/UserSwitchDialog';
import { SubscriptionCodeDialog } from './components/SubscriptionCodeDialog';
import { SupportButton } from './components/SupportButton';

// Import existing components
import assetsService from '@services/assetsService';
import { useStoreActions, useStoreState } from '@models/RootStore';
import TwoFactorAuthDialog from '../TwoFactorAuthDialog/TwoFactorAuthDialog';
import ReportProblem from './../../screens/ReportProblem/ReportProblem';

const UnifiedAcceptInvite = ({ theme = 'light' }) => {
  const history = useHistory();

  // Store state and actions
  const configFile = useStoreState((s) => s.config);
  const logout = useStoreActions((a) => a.doLogout);
  const currUser = useStoreState((s) => s.users.me);

  // Custom hooks
  const {
    inviteToken,
    inviteInfo,
    userMode,
    isLoading: isLoadingInvite,
    error: inviteError,
    expired,
  } = useInviteInfo();

  const { lists } = useUserPreferences();

  const {
    isSubmitting: isSubmittingForm,
    formError,
    loginState,
    subCode,
    setSubCode,
    acceptInvite,
    handleLogin,
    handleInviteAcceptance,
  } = useInviteActions(inviteInfo, inviteToken);

  const {
    open2FADialog,
    error2FA,
    setOpen2FADialog,
    cancel2FA,
    handleVerify2FA,
  } = use2FA();

  // State to capture 2FA input value
  const [twoFACode, setTwoFACode] = useState('');

  // Custom handler to capture 2FA input and verify
  const handle2FAInputChange = (value) => {
    setTwoFACode(value);
  };

  const handle2FAConfirm = () => {
    if (twoFACode) {
      handleVerify2FA(twoFACode);
      setTwoFACode(''); // Reset after submission
    }
  };

  const { showAcceptOptions, inputSubCode, setShowAcceptOptions } =
    useExistingUserFlow(
      inviteInfo,
      inviteToken,
      expired,
      acceptInvite,
      subCode
    );

  // Local state
  const [shouldNavigateToLogin, setShouldNavigateToLogin] = useState(false);
  const [openReport, setOpenReport] = useState(false);

  // Memoized form initial values
  const initialFormValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: inviteInfo?.email || '',
      password: '',
      confirmPassword: '',
      subscriptionCode: '',
      phone: '',
      timeFormat: 0,
      dateFormat: 0,
      temperatureScale: 1,
    }),
    [inviteInfo?.email]
  );

  const initialLoginValues = useMemo(
    () => ({
      username: inviteInfo?.email || '',
      password: '',
      subscriptionCode: '',
    }),
    [inviteInfo?.email]
  );

  // Event handlers
  const handleNavigateToLogin = useCallback(() => {
    setShouldNavigateToLogin(true);
  }, []);

  const handleForgotPassword = useCallback(() => {
    history.push('/forgot-password');
  }, [history]);

  const logoutAndRefresh = useCallback(async () => {
    await logout();
    window.location.reload();
  }, [logout]);

  const goToHome = useCallback(() => {
    setOpen2FADialog(false);
    setShowAcceptOptions(false);
    window.location.href = '/';
  }, []);

  const handleLoginWith2FA = useCallback(
    async (values) => {
      try {
        const res = await handleLogin(values);
        const is2FA = !!res?.is2FA;
        setOpen2FADialog(is2FA);
      } catch (error) {
        // Error is already handled in the hook
      }
    },
    [handleLogin, setOpen2FADialog]
  );

  // Memoized render functions
  const renderInteractionSection = useCallback(() => {
    if (!userMode) return null;

    switch (userMode) {
    case 'new':
      return (
        <NewUserForm
          theme={theme}
          inviteInfo={inviteInfo}
          initialValues={initialFormValues}
          onSubmit={handleInviteAcceptance}
          isSubmitting={isSubmittingForm}
          onForgotPassword={handleForgotPassword}
          lists={lists}
        />
      );

    case 'existing':
      return (
        <ExistingUserForm
          theme={theme}
          inviteInfo={inviteInfo}
          initialValues={initialLoginValues}
          onSubmit={handleLoginWith2FA}
          isSubmitting={isSubmittingForm}
          loginState={loginState}
          onForgotPassword={handleForgotPassword}
        />
      );

    default:
      return null;
    }
  }, [
    userMode,
    theme,
    inviteInfo,
    initialFormValues,
    initialLoginValues,
    handleInviteAcceptance,
    handleLoginWith2FA,
    isSubmittingForm,
    loginState,
    handleForgotPassword,
    lists,
  ]);

  // Handle navigation redirects
  if (shouldNavigateToLogin) {
    return <Redirect to="/" />;
  }

  // Show loading state while fetching invite info
  if (isLoadingInvite) {
    return (
      <AuthLayout
        theme={theme}
        title={t`Loading Invitation`}
        subtitle={t`Please wait while we load your invitation details...`}
        logo={assetsService.getResourcePath('logo.png')}
        onLogoError={() => {
          // Handle logo error silently
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="200px"
          style={{ gap: '16px' }}
        >
          <CircularProgress
            size={60}
            thickness={4}
            style={{
              color: theme === 'dark' ? '#ffffff' : '#1976d2',
            }}
          />
          <Box
            component="p"
            style={{
              color: theme === 'dark' ? '#ffffff' : '#666666',
              fontSize: '16px',
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t`Loading invitation details...`}
          </Box>
        </Box>
      </AuthLayout>
    );
  }

  // Show error state if invite loading failed
  if (inviteError) {
    return (
      <AuthLayout
        theme={theme}
        title={t`Invitation Error`}
        subtitle={t`Unable to load invitation details`}
        logo={assetsService.getResourcePath('logo.png')}
        onLogoError={() => {
          // Handle logo error silently
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="200px"
          style={{ gap: '16px' }}
        >
          <AuthMessage theme={theme} type="error" message={inviteError} />

          {/* Back to Login Link */}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <AuthLink
              theme={theme}
              onClick={handleNavigateToLogin}
              ariaLabel={t`Navigate back to login page`}
            >
              {t`Back to Login`}
            </AuthLink>
          </div>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <div
      className="accept-invite-container"
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'visible',
        width: '100%'
      }}>
      <AuthLayout
        theme={theme}
        title={
          userMode === 'new' ? t`Accept Invitation` : t`Welcome! Please login`
        }
        subtitle={
          userMode === 'new'
            ? t`Complete your account setup by providing your details`
            : t`Login to accept the invitation`
        }
        logo={assetsService.getResourcePath('logo.png')}
        onLogoError={() => {
          // Handle logo error silently
        }}
      >
        {/* Error Message - replaces form when there's an error */}
        {formError ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            style={{ gap: '16px' }}
          >
            <AuthMessage theme={theme} type="error" message={formError} />

            {/* Back to Login Link */}
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <AuthLink
                theme={theme}
                onClick={handleNavigateToLogin}
                ariaLabel={t`Navigate back to login page`}
              >
                {t`Back to Login`}
              </AuthLink>
            </div>
          </Box>
        ) : (
          <>
            {/* Render appropriate form based on user mode */}
            {!showAcceptOptions && renderInteractionSection()}

            {/* Back to Login Link */}
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <AuthLink
                theme={theme}
                onClick={handleNavigateToLogin}
                ariaLabel={t`Navigate back to login page`}
              >
                {t`Back to Login`}
              </AuthLink>
            </div>

            {/* Additional Information */}
            <div
              style={{
                textAlign: 'center',
                marginTop: '24px',
                fontSize: '14px',
                color: theme === 'dark' ? '#cccccc' : '#666666',
              }}
            >
              <p>
                {t`By creating an account, you agree to our terms of service and privacy policy.`}
              </p>
            </div>
          </>
        )}
      </AuthLayout>

      {/* Support Button */}
      <SupportButton
        theme={theme}
        configFile={configFile}
        onReportProblem={() => setOpenReport(true)}
      />

      {/* Report Problem Dialog */}
      {openReport && (
        <ReportProblem
          openReport
          location={'login'}
          onClose={() => setOpenReport(false)}
        />
      )}

      {/* User Switch Options Dialog */}
      <UserSwitchDialog
        open={showAcceptOptions}
        currentUserEmail={currUser?.email || ''}
        inviteEmail={inviteInfo?.email || ''}
        onIgnore={goToHome}
        onLogout={() => logout().then(() => window.location.reload())}
      />

      {/* Subscription Code Input Dialog */}
      <SubscriptionCodeDialog
        open={inputSubCode}
        subscriptionCode={subCode}
        error={formError}
        onCodeChange={setSubCode}
        onLogout={logoutAndRefresh}
        onAccept={() => acceptInvite(inviteToken, subCode)}
      />

      {/* 2FA Dialog */}
      <TwoFactorAuthDialog
        onCancel={cancel2FA}
        onConfirm={handle2FAConfirm}
        onInputChange={handle2FAInputChange}
        onClose={cancel2FA}
        title={t`Additional authentication required`}
        text={t`Your account requires an additional authentication code that was sent to your email address. Please enter the code below`}
        confirmLabel={t`Submit`}
        cancelLabel={t`Cancel`}
        open={open2FADialog}
        errorText={error2FA}
      />
    </div>
  );
};

export default UnifiedAcceptInvite;
